/* eslint-disable ember/no-computed-properties-in-native-classes */
/* eslint-disable ember/no-runloop */
import { computed, set } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { PrismMessage } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import getChartColor from 'frontend-utils/utils/hotel-chart-color';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import HotelStrategyTooltipsTooltipChart from 'lumen/components/hotel/strategy/tooltips/tooltip-chart';
import HotelStrategyTooltipsTooltipChartDetailView from 'lumen/components/hotel/strategy/tooltips/tooltip-chart-detail-view';
import { DAY_OF_WEEK_TRANSLATIONS } from 'lumen/utils/constants/general-constants';
import prepareTooltipData from 'lumen/utils/strategy/tooltip-data-format-helpers';

interface HotelStrategyChartsChartDayOfWeekSignature {
  Args: {
    containerHeight: unknown;
    data: unknown;
    isDetailView: unknown;
    onUpdateChartReference: unknown;
  };
}

export default class HotelStrategyChartsChartDayOfWeek extends Component<HotelStrategyChartsChartDayOfWeekSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare rsSelections: Services['rs-selections'];

  @tracked hoveredDayOfWeek = null;

  // We need to do this because this chart is re-used in the RS detail view.
  dummyChartDetailTooltipDivId = `dummy-tooltip-detail-for-rate-strategy-dow-chart-${guidFor(
    this,
  )}`;

  // At that moment the same chart can be in the DOM twice, and we can't have duplicate ids
  dummyChartSummaryTooltipDivId = `dummy-tooltip-summary-for-rate-strategy-dow-chart-${guidFor(
    this,
  )}`;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    const seriesMap = {};

    // First add the own hotel series
    const ownHotelSeries = {
      // @ts-expect-error: Incorrect type
      name: this.ownHotelInfo.name,
      // @ts-expect-error: Incorrect type
      hotelinfo: this.ownHotelInfo.id,
      data: [],
      type: 'line',
      zIndex: 2,
      lineWidth: 5,
      color: this.args.isDetailView
        ? getChartColor(
            // @ts-expect-error: Incorrect type
            this.ownHotelInfo.id,
            this.hotelAndMarketService.primaryAndSecondaryCompetitors,
          )
        : 'var(--prism-color-chart-accent-primary-figure-default)',
      marker: {
        enabled: true,
        radius: 5,
      },
    };

    const series = [ownHotelSeries];

    // @ts-expect-error: Incorrect type
    seriesMap[this.ownHotelInfo.id] = ownHotelSeries;

    // Then all of the competitors (activecompset will be the primary compset)
    this.rsSelections.filteredCompetitors.forEach((competitor) => {
      const competitorSeries = {
        name: competitor.name,
        hotelinfo: competitor.id,
        data: [],
        type: 'line',
        lineWidth: 2,
        color: this.args.isDetailView
          ? getChartColor(
              // @ts-expect-error: Incorrect type
              competitor.id,
              this.hotelAndMarketService.primaryAndSecondaryCompetitors,
            )
          : 'var(--prism-color-chart-sequential-info-02)',
        zIndex: 1,
        marker: {
          enabled: true,
          radius: 4,
        },
      };

      series.push(competitorSeries);
      // @ts-expect-error: Incorrect type
      seriesMap[competitor.id] = competitorSeries;
    });

    // Here we will keep the values for the areaRange
    // @ts-expect-error: Incorrect type
    const minMaxPerDay = [];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.sortedDays.forEach((day) => {
      // Dummy values for the initial min and maxes
      let max = 0;
      let min = Infinity;

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      day.filteredPatterns.forEach(({ rate, hotelinfo, hasNoData }) => {
        // First check if what the API returns is actually in the compset/own hotel
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const currentSeries = seriesMap[hotelinfo.id];
        if (currentSeries) {
          if (hasNoData) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            currentSeries.data.push({ y: null });
          } else {
            // The rate is for the scatter charts
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            currentSeries.data.push({ y: rate });
            if (rate < min) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              min = rate;
            }

            if (rate > max) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              max = rate;
            }
          }
        }
      });

      minMaxPerDay.push([min, max]);
    });

    if (!this.args.isDetailView) {
      series.push({
        type: 'arearange',
        name: 'range',
        color: 'var(--prism-color-chart-sequential-info-02)',
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        data: minMaxPerDay,
      });
    }

    return series;
  }

  @computed(
    'args.{containerHeight,isDetailView}',
    'sortedDays.[]',
    'value',
    'x',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    // This will determine the categories of the chart (aka the days)
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    const weekdays = this.sortedDays.map((day) => day.label);

    const chartOptions = {
      chart: {
        type: 'arearange',
      },
      plotOptions: {
        arearange: {
          fillColor: 'var(--prism-color-chart-sequential-info-01)',
          opacity: 0.4,
        },
      },
      xAxis: {
        type: 'category',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        categories: weekdays,
        labels: {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter() {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            const key = DAY_OF_WEEK_TRANSLATIONS[this.value];

            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-return
            return that.intl.exists(key)
              ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                that.intl.t(key).toString()
              : // @ts-expect-error: Incorrect type
                this.value;
          },
        },
      },
      yAxis: {
        title: {
          text: '',
        },
        labels: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter() {
            const label = that.hotelAndMarketService.formatRate(
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              this.value,
              true,
              false,
            );

            return label === null ? label : `${label}`;
          },
        },
      },
      tooltip: {
        hideDelay: 0.2,
        padding: 0,
        borderWidth: 0,
        shadow: false,
        shared: true,
        distance: 20,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          // IE11 hack #DROP-IE
          begin();
          set(that, 'isVisible', false);
          end();

          begin();
          set(that, 'isVisible', true);
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          that.hoveredDayOfWeek = this.x;
          end();

          if (that.args.isDetailView) {
            // @ts-expect-error: Incorrect type
            return document.getElementById(that.dummyChartDetailTooltipDivId)
              .innerHTML;
          }

          // @ts-expect-error: Incorrect type
          return document.getElementById(that.dummyChartSummaryTooltipDivId)
            .innerHTML;
        },
        useHTML: true,
      },
      legend: {
        enabled: this.args.isDetailView,
        itemStyle: {
          color: 'var(--prism-color-text-neutral-subdued)',
        },
        itemHoverStyle: {
          color: 'var(--prism-color-interactive-text-secondary-hover)',
        },
      },
    };

    // These charts are used in both the RS overview & detail modals. Height is required in the modal scenario
    if (this.args.containerHeight) {
      // @ts-expect-error: Incorrect type
      chartOptions.chart.height = this.args.containerHeight;
    }

    // Reduce number of labels for this chart in the summary view
    if (!this.args.isDetailView) {
      // @ts-expect-error: Incorrect type
      chartOptions.yAxis.labels.step = 2;
    }

    return chartOptions;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dataForHoveredDayOfWeekTooltip() {
    let tooltipData = [];

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.hoveredSubtypeData.length) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      tooltipData = prepareTooltipData(
        this.hotelAndMarketService,
        this.intl,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.hoveredSubtypeData[0],
        true,
      );
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return tooltipData;
  }

  // Properties used for detail tooltip:
  // - hoveredSubtypeData
  // - dataForHoveredDayOfWeekTooltip
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hoveredSubtypeData() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.sortedDays.filter(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (entry) => entry.label === this.hoveredDayOfWeek,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get labelForHoveredDayOfWeek() {
    // On initial load (even before hovering) hoveredDayOfWeek is null
    if (this.hoveredDayOfWeek === null) {
      return '';
    }
    return this.intl
      .t(DAY_OF_WEEK_TRANSLATIONS[this.hoveredDayOfWeek])
      .toString();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownHotelInfo() {
    return this.hotelAndMarketService.hotel?.hotelinfo;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get positionForHoveredDayOfWeek() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.positionPerDayOfWeek.get(this.hoveredDayOfWeek);
  }

  // Properties used for summary tooltip:
  // - positionPerDayOfWeek
  // - positionForHoveredDayOfWeek
  // - labelForHoveredDayOfWeek
  // Without this decorator positionPerDayOfWeek is executed every time positionForHoveredDayOfWeek/hoveredDayOfWeek is updated
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get positionPerDayOfWeek() {
    const positionMap = new Map();

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.sortedDays.forEach((day) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      positionMap.set(day.label, {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        subtypePosition: day.subtypePosition,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        formattedSubtypePosition: day.formattedSubtypePosition,
      });
    });

    return positionMap;
  }

  @computed('args.data.filteredSubtypes')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedDays() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.data?.filteredSubtypes;
  }

  <template>
    {{! @glint-expect-error: Incorrect type }}
    {{#if @data.isMissing}}
      <div
        class="rate-strategy-empty-state-container flex items-center justify-center"
      >
        <PrismMessage
          @description={{t "stategy.empty_state.partial.message"}}
          @illustration="100x100_progress"
          @title={{t "general.empty_state.title.crunching_numbers"}}
        />
      </div>
    {{else}}
      <GeneralChartsOtaChart
        {{! @glint-expect-error: Incorrect type }}
        @chartOptions={{this.chartOptions}}
        @model={{this.chartData}}
        {{! @glint-expect-error: Incorrect type }}
        @onUpdateChartReference={{@onUpdateChartReference}}
      />

      {{#if @isDetailView}}
        <div class="hidden" id={{this.dummyChartDetailTooltipDivId}}>
          <HotelStrategyTooltipsTooltipChartDetailView
            @dataForTooltip={{this.dataForHoveredDayOfWeekTooltip}}
            @ratesColumnHeaderTranslation={{t "rates.rate"}}
            @tooltipLabel={{this.labelForHoveredDayOfWeek}}
          />
        </div>
      {{else}}
        <div class="hidden" id={{this.dummyChartSummaryTooltipDivId}}>
          {{! @glint-expect-error: Incorrect type }}
          {{#if this.isVisible}}
            <HotelStrategyTooltipsTooltipChart
              @label={{this.labelForHoveredDayOfWeek}}
              @position={{this.positionForHoveredDayOfWeek}}
            />
          {{/if}}
        </div>
      {{/if}}
    {{/if}}
  </template>
}
